import { api } from "./api";
import { isUserActiveAndVisible, onUserActivityChange } from "./userActivity";

const debug = false; // Enable to see refresh and activity logs
const useCache = false; // whether to store user data in local storage
// NOTE: remember to disable API key storing in local storage cause bad practice

const refreshUserInterval: number | undefined = 1000 * 60 * 1; // the interval at which the user data is refreshed (fetched again from the server) [ undefined to disable ]
// this keeps the user's data up-to-date, and it'll also only happen while the tab is active (and the user is actually using the site) 

// Types

export type UserController = USER<{
    /** Entry to clarify that this is a user controller */
    isUserController: true;
    /** Update the user with new data */
    update: (newUser: USER) => void;
    /** Get an API key from the user, reliably */
    getApiKey: () => Promise<string | undefined>;
}>

export type GetUserOptions = {
    /** Specified if it's only necassery to fetch minified content, results in faster processing */
    minified?: boolean;
    /** Whether to keep this callback function alive for future user update events */
    keepAlive?: boolean;
}
type GetUserCallback = (user?: UserController, err?: string) => Promise<void> | void;

// Objects
const defaultGetUserOptions: GetUserOptions = {
    minified: false
}

const CACHE = {
    currentlyFetching: new Map<string, GetUserCallback[]>(),
    listeners: new Map<string, GetUserCallback[]>(),
}

// HELPER FUNCTIONS

async function getUserApiKey(user: USER) {
    const apiKey = user.apiKeys[0]!;
    if (apiKey) return { key: apiKey, isNew: false };

    console.warn("No API key, will request for one");

    try {
        const response = await api.request("/api/user/apikey");
        if (response.status != 200) return console.warn("Failed to get API key, exiting");

        const json = await response.json();
        const newApiKey = json.apiKey;

        return { key: newApiKey as string, isNew: true };
    } catch (error) {
        console.error("Failed to get API key: ", error);
        return undefined;
    }
}

// MAIN FUNCTIONS

/** LocalStorage handler */
export const storage = {
    /** Fetches an item from local storage */
    get: (key: string) => {
        const item = localStorage.getItem(key);
        if (!item) return undefined;

        try {
            return JSON.parse(item);
        } catch (error) {
            console.warn("Failed to parse item from storage: ", error);
            return undefined;
        }
    },
    /** Sets a value in local storage */
    set: (key: string, value: any) => {
        try {
            localStorage.setItem(key, JSON.stringify(value));
            return true;
        } catch (error) {
            console.warn("Failed to set item to storage: ", error);
            return false;
        }
    },
    /** Deletes a value in local storage */
    delete: (key: string) => {
        localStorage.removeItem(key);
    }
}

/** Fetch user using callback function, if callback returns undefined then error happened */
export function getUserCallback(cb: GetUserCallback, options?: GetUserOptions) {
    // Format Options
    if (!options) options = defaultGetUserOptions;
    else options = { ...defaultGetUserOptions, ...options };

    // Process Options
    const storagePath = options.minified ? 'user_min' : 'user';

    // Attach to listeners if keep-alive
    if (options.keepAlive) {
        const listeners = CACHE.listeners.get(storagePath) || [];
        listeners.push(cb);
        CACHE.listeners.set(storagePath, listeners);
    }

    // Check for currently fetching
    if (CACHE.currentlyFetching.has(storagePath)) {
        if (debug) console.log("[STORE] Already fetching user, attach to queue");
        CACHE.currentlyFetching.get(storagePath)!.push(cb);
        return;
    }

    // Check Cache
    const cachedUser = localStorage.getItem(storagePath);
    if (cachedUser && useCache) {
        try {
            cb(JSON.parse(cachedUser));
        } catch (error) {
            console.warn("Skipping cache fetch: Failed to parse cached user -> ", error);
        }
    } else if (!useCache) {
        // delete cache if it exists
        localStorage.removeItem(storagePath);
    }

    // Add to currently fetching
    CACHE.currentlyFetching.set(storagePath, [cb]);

    function callCallbackFuncs(user?: USER, err?: string) {
        // embed helper functions
        if (user && !("update" in user)) user = {
            ...user,
            isUserController: true,
            update: (newUser: USER) => {
                if (useCache) localStorage.setItem(storagePath, JSON.stringify(newUser));
                callCallbackFuncs(newUser);
            },
            getApiKey: async () => {
                if (!user) return console.warn("No user to get API key from");

                const result = await getUserApiKey(user);
                if (!result) return console.warn("Failed to get API key");

                const { key, isNew } = result;
                if (isNew) {
                    user.apiKeys.push(key);
                    localStorage.setItem(storagePath, JSON.stringify(user));
                    callCallbackFuncs(user);
                }

                return key;
            },
        } as UserController;

        // run callbacks
        const callbackFunctions = [CACHE.currentlyFetching.get(storagePath), CACHE.listeners.get(storagePath)].flat().filter(e => e !== undefined);
        if (!callbackFunctions || callbackFunctions.length <= 0) return console.warn("No callback functions specified for getUserCallback");
        Promise.all(callbackFunctions.map(cb => cb(user as (UserController | undefined), err))).finally(() => CACHE.currentlyFetching.delete(storagePath));
    }

    // Fetch User
    api.request("/api/user/from/jwt" + (options.minified ? "/min" : "")).then(res => {
        if (res.status == 200) {
            res.json().then((user: USER) => {
                localStorage.setItem(storagePath, JSON.stringify(user));
                callCallbackFuncs(user);
            });
        } else if (res.status == 401) {
            // Likely not logged in
            localStorage.removeItem(storagePath);
            callCallbackFuncs(undefined, "unauthorized");
        } else if (res.status == 503) {
            // Likely server offline
            callCallbackFuncs(undefined, "offline");
        } else {
            console.error("Unknown failure reason for fetching user: ", res);
            callCallbackFuncs(undefined, "unknown");
        }
    }).catch((err: Error) => {
        if (err.message == "Failed to fetch") {
            // Likely offline
            callCallbackFuncs(undefined, "offline");
            return;
        }

        console.error(`Failed to fetch user: ${err.message}`);
        localStorage.removeItem(storagePath);
        callCallbackFuncs(undefined, "unknown_error");
    });
}

// User Refreshing - Auto-refresh user data with smart activity-based pausing
if (refreshUserInterval) {
    if (debug) console.log("[STORE] User refresh interval set to: ", refreshUserInterval);

    let refreshInterval: NodeJS.Timeout | undefined;
    let userInactiveSince: number | null = null;

    /** Refresh user data */
    function refreshUserDataNow() {
        getUserCallback(() => { }, { minified: false, keepAlive: false });
    }

    /** Start the periodic user data refresh */
    function startUserRefresh() {        
        refreshInterval = setInterval(() => {
            if (debug) console.log("[STORE] Refreshing user data...");
            refreshUserDataNow();
        }, refreshUserInterval);
    }

    /** Stop the periodic user data refresh */
    function stopUserRefresh() {        
        if (!refreshInterval) return;
        if (debug) console.log("[STORE] Stopping user refresh");
        clearInterval(refreshInterval);
        refreshInterval = undefined;
    }

    /** Check if user was inactive longer than the refresh interval */
    function needsCatchUpRefresh(): boolean {
        return userInactiveSince !== null &&
            Date.now() - userInactiveSince > refreshUserInterval!;
    }

    // Start initial refresh cycle
    startUserRefresh();

    // Handle activity changes
    onUserActivityChange((isActive) => {        
        if (!isActive && refreshInterval) {
            // User became inactive - pause refreshing
            if (debug) console.log("[STORE] User became inactive, stopping user refresh");
            userInactiveSince = Date.now();
            stopUserRefresh();

        } else if (isActive && !refreshInterval) {
            // User became active - resume refreshing
            if (debug) console.log("[STORE] User became active, resuming user refresh");

            // If user was away too long, refresh immediately to catch up
            if (needsCatchUpRefresh()) {
                if (debug) console.log("[STORE] Catching up on missed refresh");
                refreshUserDataNow();
            }

            startUserRefresh();
            userInactiveSince = null;
        }
    });
}